import invariant from "tiny-invariant"
import { formatEther, parseEther } from "ethers/lib/utils.js"

import type {
  ExecutableCallForBlockchain,
  TallyActionArgs,
} from "createProposal/action/types"
import type {
  Account,
  ExecutableCallInput,
  ExecutableCallRewards,
} from "query/graphql"
import { ExecutableCallType } from "query/graphql"
import TallyAction from "createProposal/action/TallyAction"
import { getAccountAddress } from "web3/helpers/accountId"
import { getTallyRewardRecipient } from "createProposal/helpers/reward"

type TransferNativeAssetArgs = {
  rewardsRecipients?: RewardRecipient[]
  tallyRewardRecipient?: RewardRecipient
  contributorFee?: number
  tallyFee?: number
} & TallyActionArgs

type RewardRecipient = Pick<Account, "address" | "picture" | "name"> & {
  rewardValue: string
}

class TransferNativeAssetAction extends TallyAction {
  rewardsRecipients?: RewardRecipient[]
  contributorFee?: number
  tallyFee?: number

  constructor(args?: TransferNativeAssetArgs) {
    super(args)

    this.recipe = ExecutableCallType.Nativetransfer

    if (args?.tallyRewardRecipient) {
      if (args?.rewardsRecipients) {
        this.rewardsRecipients = args.rewardsRecipients
      } else {
        this.rewardsRecipients = [args.tallyRewardRecipient]
      }
    }

    if (args?.contributorFee) {
      this.contributorFee = args.contributorFee
    }

    if (args?.tallyFee) {
      this.tallyFee = args.tallyFee
    }
  }

  setRewardsRecipients(rewardsRecipients?: RewardRecipient[]): this {
    if (rewardsRecipients) this.rewardsRecipients = rewardsRecipients

    return this
  }

  setContributorFee(contributorFee?: number): this {
    if (contributorFee) this.contributorFee = contributorFee

    return this
  }

  setTallyFee(tallyFee?: number): this {
    if (tallyFee) this.tallyFee = tallyFee

    return this
  }

  getTarget(): string {
    const { targetAddress } = this

    invariant(
      typeof targetAddress === "string",
      "Transfer Native Asset Recipe does not support multiple targets.",
    )

    return targetAddress
  }

  encodeForBlockchain():
    | ExecutableCallForBlockchain
    | ExecutableCallForBlockchain[]
    | undefined {
    const encodeTransfer = super.encodeForBlockchain()

    if (!encodeTransfer) {
      return undefined
    }

    if (this.rewardsRecipients && this.tallyFee) {
      const data = this.getCalldata()
      invariant(
        typeof data === "string",
        "This action requires a single calldata.",
      )

      const method = this.getSignature()
      invariant(
        typeof method === "string",
        "This action requires a single signature.",
      )

      const value = this.getValue()
      invariant(
        typeof value === "string",
        "This action requires a single signature.",
      )

      const rewardRecipientsExecutableCalls = this.rewardsRecipients.map(
        (rewardsRecipient) => this.encodeRewardForBlockchain(rewardsRecipient),
      )

      const target = this.getTarget()
      const payloadLength = Array.isArray(target) ? target.length : 1

      if (payloadLength > 1) {
        return (encodeTransfer as ExecutableCallForBlockchain[]).concat(
          rewardRecipientsExecutableCalls,
        )
      }

      return [
        encodeTransfer as ExecutableCallForBlockchain,
        ...rewardRecipientsExecutableCalls,
      ]
    }

    return encodeTransfer
  }

  encodeForTallyApi(): ExecutableCallInput | ExecutableCallInput[] | undefined {
    const { recipe, rewardsRecipients, contributorFee, tallyFee } = this

    const FEE_MULTIPLIER = 10000

    const isRewards = Boolean(rewardsRecipients?.length)

    invariant(recipe, "recipe not defined")

    const blockchainData = this.encodeForBlockchain()

    if (!blockchainData) {
      return undefined
    }

    if (Array.isArray(blockchainData)) {
      return blockchainData.map((blockchainDataItem, idx) => ({
        target: blockchainDataItem.target,
        calldata: blockchainDataItem.calldata,
        signature: blockchainDataItem.signature,
        value: blockchainDataItem.value,
        type: isRewards && idx > 0 ? ExecutableCallType.Reward : recipe,
        offchaindata:
          isRewards && idx === 0
            ? JSON.stringify({
                contributorFee: contributorFee
                  ? Math.round(contributorFee * FEE_MULTIPLIER)
                  : undefined,
                tallyFee: tallyFee
                  ? Math.round(tallyFee * FEE_MULTIPLIER)
                  : undefined,
                recipients: rewardsRecipients?.map((rewardsRecipient) => {
                  return rewardsRecipient.address
                }),
              })
            : undefined,
      }))
    }

    return {
      target: blockchainData.target,
      calldata: blockchainData.calldata,
      signature: blockchainData.signature,
      value: blockchainData.value,
      type: recipe,
    }
  }

  encodeRewardForBlockchain(rewardsRecipient: RewardRecipient) {
    const data = this.getCalldata()
    invariant(
      typeof data === "string",
      "This action requires a single calldata.",
    )

    const method = this.getSignature()
    invariant(
      typeof method === "string",
      "This action requires a single signature.",
    )

    const valueInWei = parseEther(
      rewardsRecipient.rewardValue.toString(),
    ).toString()

    return {
      target: rewardsRecipient.address,
      calldata: data,
      signature: method,
      value: valueInWei,
    }
  }

  async populateRewardsRecipients(
    executableAction: ExecutableCallInput,
  ): Promise<this> {
    const meta = executableAction?.offchaindata as ExecutableCallRewards

    if (meta) {
      if ("contributorFee" in meta) {
        const contributorFee = meta.contributorFee
        this.setContributorFee(contributorFee / 10000)
      }

      if ("tallyFee" in meta) {
        const tallyFee = meta.tallyFee
        this.setTallyFee(tallyFee / 10000)
      }
    }

    return this
  }

  static async from(
    executableAction: ExecutableCallInput,
    chainId: string,
  ): Promise<TransferNativeAssetAction> {
    const nextAction = new TransferNativeAssetAction({
      targetAddress: getAccountAddress(executableAction.target),
      value: formatEther(executableAction.value as string),
      tallyRewardRecipient: getTallyRewardRecipient(chainId),
    })

    await nextAction.populateRewardsRecipients(executableAction)

    return nextAction
  }
}

export default TransferNativeAssetAction
